import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import axios from 'axios';
import { Sparkle, PaperPlaneTilt } from '@phosphor-icons/react';
import { MIGRATION_CRUNCHY } from './crunchyConfigs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Get-or-create a stable visitor_id in localStorage so anonymous chats
 *  can hold context across messages and across tabs. */
function getVisitorId() {
  let v = localStorage.getItem('mc_visitor_id');
  if (!v) {
    // RFC4122-ish: 32 hex chars, deterministic enough for our use
    v = 'v_' + Date.now().toString(36) + '_' +
        Math.random().toString(36).slice(2, 10) +
        Math.random().toString(36).slice(2, 10);
    localStorage.setItem('mc_visitor_id', v);
  }
  return v;
}

/** Load saved chat history for the current visitor, if any. */
function loadSavedChat(cfg) {
  try {
    const raw = localStorage.getItem(cfg.historyKey);
    if (!raw) return { messages: [], conversationId: null };
    const parsed = JSON.parse(raw);
    // Defensive: only restore if it's a valid shape with array of messages.
    if (Array.isArray(parsed.messages)) {
      return {
        messages: parsed.messages,
        conversationId: localStorage.getItem(cfg.convKey) || null,
      };
    }
  } catch (_) {
    // Corrupt — silently drop and start fresh.
  }
  return { messages: [], conversationId: null };
}

/**
 * Embedded Crunchy chat panel. Config-driven (see crunchyConfigs.js):
 *  - MIGRATION_CRUNCHY (default): the /migrate concierge. Authed users use
 *    /ai/migration/chat (150 msg/mo budget); anon uses /ai/migration/chat-anon.
 *  - SALES_CRUNCHY: the SalesCrunchCRM pre-sales rep — always anonymous.
 * Anonymous mode: 20 msg/day per IP for abuse protection.
 *
 * Chat history is persisted to localStorage so closing and reopening the
 * drawer does NOT wipe the user's conversation context (fix B-MC-001).
 */
export default function MigrationConcierge({ cfg = MIGRATION_CRUNCHY }) {
  const saved = loadSavedChat(cfg);
  const [messages, setMessages] = useState(saved.messages);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [conversationId, setConversationId] = useState(saved.conversationId);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  // Persist every message change. Cap at 50 messages so the localStorage
  // entry doesn't grow forever for chatty sessions.
  useEffect(() => {
    try {
      const trimmed = messages.slice(-50);
      localStorage.setItem(cfg.historyKey, JSON.stringify({ messages: trimmed }));
      if (conversationId) localStorage.setItem(cfg.convKey, conversationId);
    } catch (_) {
      // Quota exceeded or storage disabled — chat still works in-memory.
    }
  }, [messages, conversationId, cfg]);

  // Manual "start over" handler — clears persisted history.
  const resetChat = () => {
    setMessages([]);
    setConversationId(null);
    try {
      localStorage.removeItem(cfg.historyKey);
      localStorage.removeItem(cfg.convKey);
    } catch (_) {
      // ignore storage failures
    }
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || busy) return;
    setInput('');
    setBusy(true);
    setMessages((m) => [...m, { role: 'user', content: text }]);

    const isAuthed = !!cfg.authedPath && !!localStorage.getItem('access_token');
    try {
      let data;
      if (isAuthed) {
        // Authenticated path — uses per-user 150 msg/month budget
        const resp = await api.post(cfg.authedPath, {
          message: text,
          conversation_id: conversationId,
        });
        data = resp.data;
      } else {
        // Anonymous path — uses per-IP 20 msg/day budget. Bypass our
        // axios `api` instance so the 401 interceptor doesn't try to
        // refresh tokens we don't have.
        const resp = await axios.post(`${API_URL}${cfg.anonPath}`, {
          message: text,
          conversation_id: conversationId,
          visitor_id: getVisitorId(),
        });
        data = resp.data;
      }
      setConversationId(data.conversation_id);
      setMessages((m) => [...m, { role: 'assistant', content: data.reply, limit: data.limit_reached }]);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Something went wrong.';
      setMessages((m) => [...m, { role: 'assistant', content: `Couldn't reach the AI: ${detail}`, error: true }]);
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm flex flex-col h-[500px]" data-testid={`${cfg.tid}-concierge`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0B5394] to-[#002FA7] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
        <Sparkle size={18} weight="fill" className="text-[#F39C12]" />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">{cfg.headerTitle}</div>
          <div className="text-[10px] text-white/70">{cfg.headerSub}</div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={resetChat}
            className="text-[10px] text-white/60 hover:text-white underline whitespace-nowrap"
            data-testid={`${cfg.tid}-concierge-reset`}
            title="Start over"
          >
            Start over
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
        {messages.length === 0 && !busy && (
          <div className="text-center text-slate-500 text-sm py-6">
            <Sparkle size={28} weight="fill" className="text-[#F39C12]/40 mx-auto mb-3" />
            <p className="font-semibold text-slate-700 mb-2">Hey! I&apos;m Crunchy 🍿</p>
            <p className="text-xs leading-relaxed max-w-[280px] mx-auto mb-4">
              {cfg.emptyBody}
            </p>
            <div className="space-y-1.5 text-xs text-left max-w-[280px] mx-auto">
              <p className="text-slate-400 font-semibold uppercase tracking-wide text-[10px]">Try:</p>
              {cfg.suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="block w-full text-left px-3 py-1.5 rounded bg-white border border-slate-200 hover:border-[#0B5394] hover:bg-blue-50 text-slate-700 transition-colors"
                  data-testid={`${cfg.tid}-suggestion-${s.slice(0, 8).replace(/\s/g, '-')}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`${cfg.tid}-msg-${m.role}`}>
            <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
              m.role === 'user'
                ? 'bg-[#0B5394] text-white'
                : m.error
                  ? 'bg-red-50 border border-red-200 text-red-800'
                  : m.limit
                    ? 'bg-amber-50 border border-amber-200 text-amber-900'
                    : 'bg-white border border-slate-200 text-slate-800 shadow-sm'
            }`}>
              {m.content}
            </div>
          </div>
        ))}

        {busy && (
          <div className="flex items-center gap-2 text-xs text-slate-500" data-testid={`${cfg.tid}-thinking`}>
            <div className="flex gap-1">
              <span className="w-1.5 h-1.5 bg-[#0B5394] rounded-full animate-bounce" />
              <span className="w-1.5 h-1.5 bg-[#0B5394] rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
              <span className="w-1.5 h-1.5 bg-[#0B5394] rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
            </div>
            <span>Thinking…</span>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-slate-200 p-2.5 bg-white rounded-b-lg shrink-0">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-1.5 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={cfg.placeholder}
            disabled={busy}
            rows={1}
            className="flex-1 resize-none border border-slate-300 rounded px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0B5394] focus:border-transparent disabled:bg-slate-50 max-h-32"
            data-testid={`${cfg.tid}-input`}
            style={{ minHeight: '36px' }}
          />
          <button
            type="submit"
            disabled={busy || !input.trim()}
            className="bg-[#0B5394] hover:bg-[#094180] disabled:bg-slate-300 text-white rounded p-2 transition-colors shrink-0"
            data-testid={`${cfg.tid}-send`}
          >
            <PaperPlaneTilt size={16} weight="fill" />
          </button>
        </form>
      </div>
    </div>
  );
}
